/**
 * SAFER-i™ pricing — mirrors /app/backend/pricing_config.py (single source
 * of truth). The frontend fetches the live snapshot via GET /api/pricing
 * on mount; this file is the render-time fallback used before the fetch
 * resolves AND the typed shape used by the Signup / Billing UI.
 *
 * NEW pricing (Mar 2026):
 *   • Freelancer:    SGD 3.99/mo (no platform fee)
 *   • Organization:  SGD 499/yr  (non-refundable onboarding fee)
 *   • Units:         SGD 2.90/unit (min 1,000 initial · 100-unit add-ons)
 *   • Monthly role consumption: Worker 1u · Supervisor 3u · Manager 6u · Top 10u
 *   • Free trial:    14 days
 */

// FX rate used for the USD display equivalent. Backend authoritative value
// arrives via /api/pricing; this is the static fallback.
export const SGD_TO_USD_FALLBACK = 0.74;

export const FREELANCER_PLAN = {
  id: 'freelancer',
  name: 'Associate Consultant',
  priceSgd: 3.99,
  priceUsd: 2.95,
  billingPeriod: 'month',
  description: 'For independent consultants. SGD 3.99/month — no platform fee.',
  features: [
    'Personal SAFER-i™ workspace',
    '1 unit / month role consumption',
    'All ala-carte services available',
    'Email support',
  ],
};

export const ORGANIZATION_PLAN = {
  id: 'organization',
  name: 'Organization',
  onboardingSgd: 499.00,
  onboardingUsd: 369.26,
  billingPeriod: 'year',
  description: 'One-time annual platform usage fee. Non-refundable.',
  features: [
    'Unlimited seats',
    'Role-based monthly unit consumption',
    'Multi-tenant org workspace',
    'All ala-carte services available',
    'Priority support',
  ],
};

export const UNIT_PURCHASE = {
  unitPriceSgd: 2.90,
  unitPriceUsd: 2.15,
  minInitialUnits: 1000,
  minInitialCostSgd: 2900.00,
  minInitialCostUsd: 2146.00,
  addonBlockSize: 100,
  addonBlockCostSgd: 290.00,
  addonBlockCostUsd: 214.60,
};

export const ROLE_MONTHLY_UNITS = {
  WORKER: 1,
  SUPERVISOR: 3,
  MANAGER: 6,
  EXECUTIVE: 10,
  HQ: 10,
  ADMIN: 10,
};

export const ROLE_LABELS = {
  WORKER: 'Worker / Frontliner',
  SUPERVISOR: 'Supervisor / Team Lead',
  MANAGER: 'Manager / Department Head',
  EXECUTIVE: 'Executive / Top Management',
  HQ: 'Top Management / Executives',
  ADMIN: 'Top Management / Executives',
};

export const TRIAL_DAYS = 14;

/** Helper: render an SGD amount with the secondary USD equivalent. */
export const formatDualCurrency = (sgd, usd) => {
  if (sgd == null) return 'Contact us';
  const sgdStr = `SGD $${Number(sgd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (usd == null) return sgdStr;
  const usdStr = `~ USD $${Number(usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${sgdStr} (${usdStr})`;
};

/**
 * Normalise a backend pricing snapshot into the camelCase shape used by
 * the signup / billing components. Accepts the response from /api/pricing.
 */
export const normalizePricingSnapshot = (api) => {
  if (!api) return null;
  return {
    currency: api.currency,
    trialDays: api.trial_days ?? TRIAL_DAYS,
    freelancer: {
      id: api.freelancer?.id ?? 'freelancer',
      name: api.freelancer?.name ?? FREELANCER_PLAN.name,
      priceSgd: api.freelancer?.price_sgd ?? FREELANCER_PLAN.priceSgd,
      priceUsd: api.freelancer?.price_usd ?? FREELANCER_PLAN.priceUsd,
      billingPeriod: api.freelancer?.billing_period ?? 'month',
      description: api.freelancer?.description ?? FREELANCER_PLAN.description,
      features: api.freelancer?.features ?? FREELANCER_PLAN.features,
    },
    organization: {
      id: api.organization?.id ?? 'organization',
      name: api.organization?.name ?? ORGANIZATION_PLAN.name,
      onboardingSgd: api.organization?.onboarding_sgd ?? ORGANIZATION_PLAN.onboardingSgd,
      onboardingUsd: api.organization?.onboarding_usd ?? ORGANIZATION_PLAN.onboardingUsd,
      billingPeriod: api.organization?.billing_period ?? 'year',
      description: api.organization?.description ?? ORGANIZATION_PLAN.description,
      features: api.organization?.features ?? ORGANIZATION_PLAN.features,
    },
    units: {
      unitPriceSgd: api.units?.unit_price_sgd ?? UNIT_PURCHASE.unitPriceSgd,
      unitPriceUsd: api.units?.unit_price_usd ?? UNIT_PURCHASE.unitPriceUsd,
      minInitialUnits: api.units?.min_initial_units ?? UNIT_PURCHASE.minInitialUnits,
      minInitialCostSgd: api.units?.min_initial_cost_sgd ?? UNIT_PURCHASE.minInitialCostSgd,
      minInitialCostUsd: api.units?.min_initial_cost_usd ?? UNIT_PURCHASE.minInitialCostUsd,
      addonBlockSize: api.units?.addon_block_size ?? UNIT_PURCHASE.addonBlockSize,
      addonBlockCostSgd: api.units?.addon_block_cost_sgd ?? UNIT_PURCHASE.addonBlockCostSgd,
      addonBlockCostUsd: api.units?.addon_block_cost_usd ?? UNIT_PURCHASE.addonBlockCostUsd,
    },
    roleMonthlyUnits: api.role_monthly_units ?? ROLE_MONTHLY_UNITS,
    alacarte: api.alacarte ?? [],
  };
};
